import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
	Chip,
	useTheme,
	alpha,
	CircularProgress,
	Divider,
	TextField,
	InputAdornment,
	Collapse,
} from "@mui/material";
import RadioButtonCheckedRoundedIcon from "@mui/icons-material/RadioButtonCheckedRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import AddCardRoundedIcon from "@mui/icons-material/AddCardRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";

import { useMemo, useState, useEffect, useRef } from "react";
import { tokens } from "../../theme";

const formatMoney = (value) =>
	Number(value || 0).toLocaleString("pt-BR", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});

const formatDate = (dateStr) => {
	if (!dateStr) return "-";
	return new Date(`${dateStr}T00:00:00`).toLocaleDateString("pt-BR");
};

const formatCreatedAt = (dateStr) => {
	if (!dateStr) return "-";
	return new Date(dateStr).toLocaleDateString("pt-BR");
};

const getKitTotal = (kit) => {
	if (!kit?.kit_products?.length) return 0;

	return kit.kit_products.reduce((acc, curr) => {
		return acc + Number(curr?.quantity || 0) * Number(curr?.sell_price || 0);
	}, 0);
};

const getKitUniqueId = (kit) => {
	return kit?.id ?? kit?.pk ?? kit?.kit_number;
};

const sortNewestFirst = (kits) => {
	return [...kits].sort((a, b) => {
		const dateA = new Date(a?.created_at || a?.updated_at || 0).getTime();
		const dateB = new Date(b?.created_at || b?.updated_at || 0).getTime();
		return dateB - dateA;
	});
};

const parseMoneyInput = (value) => {
	const normalizedValue = String(value || "")
		.replace(/\s/g, "")
		.replace(/\./g, "")
		.replace(",", ".");

	const parsedValue = Number(normalizedValue);

	return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const StatusBullet = ({ active, colors, theme }) => {
	return (
		<Box
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: 0.75,
				px: 1.15,
				py: 0.55,
				borderRadius: "999px",
				fontSize: "12px",
				fontWeight: 800,
				letterSpacing: "0.02em",
				backgroundColor: active
					? alpha(colors.greenAccent[500], theme.palette.mode === "dark" ? 0.18 : 0.14)
					: alpha(colors.grey[500], theme.palette.mode === "dark" ? 0.22 : 0.12),
				color: active ? colors.greenAccent[300] : colors.grey[200],
				border: `1px solid ${active
					? alpha(colors.greenAccent[400], 0.45)
					: alpha(colors.grey[300], 0.25)
					}`,
			}}
		>
			<RadioButtonCheckedRoundedIcon sx={{ fontSize: 12 }} />
			{active ? "ATIVO" : "ENCERRADO"}
		</Box>
	);
};

const LoadingRow = ({ colors, theme }) => {
	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: "120px 120px 140px 120px 1fr",
				gap: 1.25,
				alignItems: "center",
				px: 2,
				py: 1.5,
				borderRadius: "18px",
				background:
					theme.palette.mode === "dark"
						? `linear-gradient(135deg, ${alpha(colors.primary[400], 0.7)} 0%, ${alpha(colors.primary[500], 0.95)} 100%)`
						: `linear-gradient(135deg, ${alpha(colors.blueAccent[900], 0.7)} 0%, ${alpha(colors.primary[400], 0.92)} 100%)`,
				border: `1px solid ${alpha(colors.blueAccent[400], 0.18)}`,
				overflow: "hidden",
				position: "relative",
				"&::after": {
					content: '""',
					position: "absolute",
					inset: 0,
					transform: "translateX(-100%)",
					background: `linear-gradient(90deg, transparent, ${alpha("#ffffff", 0.08)}, transparent)`,
					animation: "kitShimmer 1.6s infinite",
				},
				"@keyframes kitShimmer": {
					"100%": {
						transform: "translateX(100%)",
					},
				},
			}}
		>
			{[100, 90, 120, 80, 180].map((w, idx) => (
				<Box
					key={idx}
					sx={{
						height: 14,
						width: w,
						borderRadius: "999px",
						backgroundColor: alpha("#ffffff", 0.14),
					}}
				/>
			))}
		</Box>
	);
};

const SelectKitPromissoryModal = ({
	open,
	onClose,
	kits = [],
	onConfirm,
	onConfirmStandalone,
	isLoading = false,
}) => {
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);

	const [selectedKitId, setSelectedKitId] = useState(null);

	const [showStandaloneForm, setShowStandaloneForm] = useState(false);
	const [standaloneValue, setStandaloneValue] = useState("");
	const [standaloneDueDate, setStandaloneDueDate] = useState("");
	const [standaloneErrors, setStandaloneErrors] = useState({});

	const standaloneFormRef = useRef(null);
	const standaloneValueInputRef = useRef(null);
	const standaloneFormEndRef = useRef(null);
	const dialogContentRef = useRef(null);


	useEffect(() => {
		if (!open) {
			setSelectedKitId(null);
			setShowStandaloneForm(false);
			setStandaloneValue("");
			setStandaloneDueDate("");
			setStandaloneErrors({});
		}
	}, [open]);

	const normalizedKits = useMemo(() => {
		const prepared = (kits || []).map((kit) => ({
			...kit,
			_uiId: getKitUniqueId(kit),
			totalValue: getKitTotal(kit),
		}));

		return sortNewestFirst(prepared);
	}, [kits]);

	useEffect(() => {
		if (!open || normalizedKits.length === 0) return;

		const activeKit = normalizedKits.find((kit) => kit?.is_active === true);
		if (activeKit) {
			setSelectedKitId(activeKit._uiId);
			return;
		}

		setSelectedKitId(normalizedKits[0]._uiId);
	}, [open, normalizedKits]);

	const selectedKit = normalizedKits.find((k) => k._uiId === selectedKitId);

	const handleToggleStandaloneForm = () => {
		setStandaloneErrors({});

		setShowStandaloneForm((currentValue) => {
			return !currentValue;
		});
	};

	const handleStandaloneValueChange = (event) => {
		let value = event.target.value;

		value = value.replace(/[^\d,]/g, "");

		const commaParts = value.split(",");

		if (commaParts.length > 2) {
			value = `${commaParts[0]},${commaParts.slice(1).join("")}`;
		}

		if (commaParts[1]?.length > 2) {
			value = `${commaParts[0]},${commaParts[1].slice(0, 2)}`;
		}

		setStandaloneValue(value);

		if (standaloneErrors.value) {
			setStandaloneErrors((currentErrors) => ({
				...currentErrors,
				value: "",
			}));
		}
	};

	const handleStandaloneDueDateChange = (event) => {
		setStandaloneDueDate(event.target.value);

		if (standaloneErrors.dueDate) {
			setStandaloneErrors((currentErrors) => ({
				...currentErrors,
				dueDate: "",
			}));
		}
	};

	const handleConfirmStandalone = () => {
		const parsedValue = parseMoneyInput(standaloneValue);

		const errors = {};

		if (parsedValue <= 0) {
			errors.value = "Informe um valor maior que zero.";
		}

		if (!standaloneDueDate) {
			errors.dueDate = "Informe a data de vencimento.";
		}

		if (Object.keys(errors).length > 0) {
			setStandaloneErrors(errors);
			return;
		}

		onConfirmStandalone?.({
			value: parsedValue,
			dueDate: standaloneDueDate,
		});
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="lg"
			PaperProps={{
				sx: {
					borderRadius: "28px",
					overflow: "hidden",
					background:
						theme.palette.mode === "dark"
							? `linear-gradient(180deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`
							: `linear-gradient(180deg, #ffffff 0%, ${alpha(colors.pink[900], 0.35)} 100%)`,
					border: `1px solid ${alpha(
						theme.palette.mode === "dark" ? colors.pink[400] : colors.pink[700],
						0.18
					)}`,
					boxShadow:
						theme.palette.mode === "dark"
							? `0 20px 60px ${alpha("#000", 0.45)}`
							: `0 20px 60px ${alpha(colors.primary[100], 0.14)}`,
				},
			}}
		>
			<DialogTitle
				sx={{
					px: 3,
					py: 2.5,
					background:
						theme.palette.mode === "dark"
							? `linear-gradient(135deg, ${colors.pink[700]} 0%, ${colors.primary[400]} 55%, ${colors.blueAccent[700]} 100%)`
							: `linear-gradient(135deg, ${colors.pink[800]} 0%, ${colors.blueAccent[800]} 100%)`,
					color: "#fff",
				}}
			>
				<Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
					<Box display="flex" alignItems="center" gap={1.25}>
						<Box
							sx={{
								width: 42,
								height: 42,
								borderRadius: "14px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								backgroundColor: alpha("#fff", 0.14),
								backdropFilter: "blur(10px)",
								border: `1px solid ${alpha("#fff", 0.18)}`,
							}}
						>
							<ReceiptLongRoundedIcon />
						</Box>

						<Box>
							<Typography sx={{ fontSize: 22, fontWeight: 900, lineHeight: 1.1 }}>
								Selecionar kit
							</Typography>
							<Typography sx={{ opacity: 0.88, fontSize: 13.5, mt: 0.4 }}>
								Escolha o kit que será usado para anexar a nota promissória ao contrato
							</Typography>
						</Box>
					</Box>

					<Chip
						icon={<AutoAwesomeRoundedIcon />}
						label={`${normalizedKits.length} ${normalizedKits.length === 1 ? "kit encontrado" : "kits encontrados"}`}
						sx={{
							backgroundColor: alpha("#fff", 0.14),
							color: "#fff",
							border: `1px solid ${alpha("#fff", 0.16)}`,
							"& .MuiChip-icon": { color: "#fff" },
							fontWeight: 700,
						}}
					/>
				</Box>
			</DialogTitle>

			<DialogContent
				ref={dialogContentRef}
				sx={{
					p: 0,
					backgroundColor:
						theme.palette.mode === "dark"
							? alpha(colors.primary[500], 0.96)
							: alpha("#ffffff", 0.92),
				}}
			>
				<Box sx={{ px: 3, pt: 2.25, pb: 1.5 }}>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "120px 150px 160px 140px 1fr",
							gap: 1,
							px: 2,
							py: 1.25,
							borderRadius: "18px",
							backgroundColor:
								theme.palette.mode === "dark"
									? alpha(colors.primary[400], 0.9)
									: alpha(colors.pink[900], 0.55),
							border: `1px solid ${alpha(colors.pink[400], 0.15)}`,
							color: theme.palette.mode === "dark" ? colors.grey[200] : colors.primary[100],
							fontSize: "12px",
							fontWeight: 800,
							letterSpacing: "0.04em",
							textTransform: "uppercase",
						}}
					>
						<Box>Kit</Box>
						<Box>Status</Box>
						<Box>Valor</Box>
						<Box>Vencimento</Box>
						<Box>Detalhes</Box>
					</Box>
				</Box>

				<Box sx={{ px: 3, pb: 2.5, display: "grid", gap: 1.1 }}>
					{isLoading ? (
						<>
							<Box
								sx={{
									minHeight: 240,
									display: "flex",
									flexDirection: "column",
									gap: 1.1,
								}}
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1.25,
										mb: 1,
										px: 0.5,
										color: theme.palette.mode === "dark" ? colors.grey[200] : colors.primary[200],
									}}
								>
									<CircularProgress
										size={20}
										sx={{
											color: colors.greenAccent[400],
										}}
									/>
									<Typography sx={{ fontWeight: 700, fontSize: 14 }}>
										Buscando kits da vendedora...
									</Typography>
								</Box>

								{Array.from({ length: 4 }).map((_, idx) => (
									<LoadingRow key={idx} colors={colors} theme={theme} />
								))}
							</Box>
						</>
					) : normalizedKits.length === 0 ? (
						<Box
							sx={{
								minHeight: 220,
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								textAlign: "center",
								gap: 1,
								color: theme.palette.mode === "dark" ? colors.grey[200] : colors.primary[200],
							}}
						>
							<Box
								sx={{
									width: 66,
									height: 66,
									borderRadius: "20px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									backgroundColor: alpha(colors.pink[500], 0.14),
									border: `1px solid ${alpha(colors.pink[400], 0.18)}`,
								}}
							>
								<Inventory2RoundedIcon sx={{ fontSize: 30, color: colors.pink[300] }} />
							</Box>

							<Typography sx={{ fontWeight: 900, fontSize: 18 }}>
								Nenhum kit disponível
							</Typography>

							<Typography sx={{ maxWidth: 420, opacity: 0.8 }}>
								Não encontramos kits para esta vendedora no momento.
							</Typography>
						</Box>
					) : (
						normalizedKits.map((kit) => {
							const isSelected = selectedKitId === kit._uiId;
							const isActive = !!kit?.is_active;

							return (
								<Box
									key={kit._uiId}
									onClick={() => setSelectedKitId(kit._uiId)}
									sx={{
										display: "grid",
										gridTemplateColumns: "120px 150px 160px 140px 1fr",
										gap: 1,
										alignItems: "center",
										px: 2,
										py: 1.4,
										borderRadius: "20px",
										cursor: "pointer",
										transition: "all 0.18s ease",
										background: isSelected
											? theme.palette.mode === "dark"
												? `linear-gradient(135deg, ${alpha(colors.pink[700], 0.95)} 0%, ${alpha(colors.primary[400], 0.98)} 70%)`
												: `linear-gradient(135deg, ${alpha(colors.pink[900], 0.95)} 0%, ${alpha(colors.blueAccent[900], 0.95)} 100%)`
											: theme.palette.mode === "dark"
												? `linear-gradient(135deg, ${alpha(colors.primary[400], 0.82)} 0%, ${alpha(colors.primary[500], 0.96)} 100%)`
												: `linear-gradient(135deg, ${alpha("#ffffff", 0.96)} 0%, ${alpha(colors.pink[900], 0.28)} 100%)`,
										border: isSelected
											? `1px solid ${alpha(colors.greenAccent[400], 0.52)}`
											: `1px solid ${alpha(
												theme.palette.mode === "dark" ? colors.grey[500] : colors.pink[700],
												0.16
											)}`,
										boxShadow: isSelected
											? `0 12px 30px ${alpha(
												theme.palette.mode === "dark" ? colors.greenAccent[900] : colors.primary[100],
												0.22
											)}`
											: "none",
										"&:hover": {
											transform: "translateY(-1px)",
											borderColor: alpha(colors.greenAccent[400], 0.4),
										},
									}}
								>
									<Box>
										<Typography
											sx={{
												fontSize: 15,
												fontWeight: 900,
												color: isSelected ? "#fff" : theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[100],
											}}
										>
											#{kit?.kit_number || "-"}
										</Typography>
									</Box>

									<Box>
										<StatusBullet active={isActive} colors={colors} theme={theme} />
									</Box>

									<Box>
										<Typography
											sx={{
												fontSize: 14,
												fontWeight: 800,
												color: isSelected ? colors.greenAccent[300] : theme.palette.mode === "dark" ? colors.greenAccent[300] : colors.greenAccent[200],
											}}
										>
											R$ {formatMoney(kit.totalValue)}
										</Typography>
									</Box>

									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 0.75,
											color: isSelected ? alpha("#fff", 0.92) : theme.palette.mode === "dark" ? colors.grey[200] : colors.primary[200],
										}}
									>
										<CalendarMonthRoundedIcon sx={{ fontSize: 16 }} />
										<Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
											{formatDate(kit?.pay_date)}
										</Typography>
									</Box>

									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											gap: 1.5,
											flexWrap: "wrap",
										}}
									>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 0.75,
												color: isSelected ? alpha("#fff", 0.9) : theme.palette.mode === "dark" ? colors.grey[200] : colors.primary[200],
											}}
										>
											<Inventory2RoundedIcon sx={{ fontSize: 16 }} />
											<Typography sx={{ fontSize: 13.5 }}>
												{kit?.kit_products?.length || 0} produtos
											</Typography>
										</Box>

										<Divider
											flexItem
											orientation="vertical"
											sx={{
												display: { xs: "none", md: "block" },
												borderColor: alpha("#fff", isSelected ? 0.18 : 0.08),
											}}
										/>

										<Typography
											sx={{
												fontSize: 13,
												fontWeight: 700,
												color: isSelected ? alpha("#fff", 0.9) : theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[300],
											}}
										>
											Criado em {formatCreatedAt(kit?.created_at)}
										</Typography>

										{isSelected && (
											<Chip
												icon={<CheckCircleRoundedIcon />}
												label="Selecionado"
												sx={{
													ml: "auto",
													backgroundColor: alpha(colors.greenAccent[500], 0.18),
													color: "#fff",
													border: `1px solid ${alpha(colors.greenAccent[300], 0.35)}`,
													"& .MuiChip-icon": { color: colors.greenAccent[300] },
													fontWeight: 800,
												}}
											/>
										)}
									</Box>
								</Box>
							);
						})
					)}
				</Box>
				<Collapse
					in={showStandaloneForm}
					timeout={350}
					onEntered={() => {
						const content = dialogContentRef.current;

						if (!content) return;

						content.scrollTo({
							top: content.scrollHeight,
							behavior: "smooth",
						});

						setTimeout(() => {
							standaloneValueInputRef.current?.focus({
								preventScroll: true,
							});
						}, 400);
					}}
				>
					<Box
						ref={standaloneFormRef}
						sx={{
							mx: 3,
							mb: 2.5,
							p: 2.5,
							borderRadius: "20px",
							backgroundColor:
								theme.palette.mode === "dark"
									? alpha(colors.blueAccent[700], 0.13)
									: alpha(colors.blueAccent[900], 0.35),
							border: `1px solid ${alpha(
								colors.blueAccent[400],
								0.28
							)}`,
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								mb: 2,
							}}
						>
							<Box
								sx={{
									width: 38,
									height: 38,
									borderRadius: "12px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									backgroundColor: alpha(
										colors.blueAccent[500],
										0.18
									),
									color: colors.blueAccent[300],
								}}
							>
								<AddCardRoundedIcon />
							</Box>

							<Box>
								<Typography
									sx={{
										fontSize: 16,
										fontWeight: 900,
										color:
											theme.palette.mode === "dark"
												? colors.grey[100]
												: colors.primary[100],
									}}
								>
									Nota promissória avulsa
								</Typography>

								<Typography
									sx={{
										fontSize: 13,
										opacity: 0.75,
										color:
											theme.palette.mode === "dark"
												? colors.grey[200]
												: colors.primary[200],
									}}
								>
									Informe o valor e o vencimento sem vincular a um KIT.
								</Typography>
							</Box>
						</Box>

						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: {
									xs: "1fr",
									sm: "1fr 1fr",
								},
								gap: 2,
							}}
						>
							<TextField
								inputRef={standaloneValueInputRef}
								fullWidth
								size="small"
								label="Valor da promissória"
								value={standaloneValue}
								onChange={handleStandaloneValueChange}
								placeholder="0,00"
								error={Boolean(standaloneErrors.value)}
								helperText={standaloneErrors.value}
								inputProps={{
									inputMode: "decimal",
								}}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											R$
										</InputAdornment>
									),
								}}
							/>

							<TextField
								fullWidth
								size="small"
								type="date"
								label="Data de vencimento"
								value={standaloneDueDate}
								onChange={handleStandaloneDueDateChange}
								error={Boolean(standaloneErrors.dueDate)}
								helperText={standaloneErrors.dueDate}
								InputLabelProps={{
									shrink: true,
								}}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<EventRoundedIcon
												sx={{ fontSize: 18 }}
											/>
										</InputAdornment>
									),
								}}
							/>
						</Box>
						<Box
							ref={standaloneFormEndRef}
							sx={{ height: 1 }}
						/>
					</Box>
				</Collapse>
			</DialogContent>

			<DialogActions
				sx={{
					px: 3,
					py: 2,
					justifyContent: "space-between",
					borderTop: `1px solid ${alpha(
						theme.palette.mode === "dark" ? colors.grey[700] : colors.grey[800],
						0.18
					)}`,
					backgroundColor:
						theme.palette.mode === "dark"
							? alpha(colors.primary[400], 0.82)
							: alpha("#ffffff", 0.86),
				}}
			>
				<Box>
					{selectedKit && (
						<Typography
							sx={{
								fontSize: 13,
								fontWeight: 700,
								color: theme.palette.mode === "dark" ? colors.grey[200] : colors.primary[200],
							}}
						>
							Kit selecionado: #{selectedKit?.kit_number || "-"}
						</Typography>
					)}
				</Box>

				<Box
					display="flex"
					gap={1}
					flexWrap="wrap"
					justifyContent="flex-end"
				>
					<Button
						onClick={onClose}
						variant="outlined"
						sx={{
							borderRadius: "999px",
							px: 2.2,
							fontWeight: 800,
							borderColor: alpha(
								theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[200],
								0.28
							),
							color: theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[200],
						}}
					>
						Cancelar
					</Button>

					<Button
						onClick={handleToggleStandaloneForm}
						variant={showStandaloneForm ? "contained" : "outlined"}
						startIcon={<AddCardRoundedIcon />}
						sx={{
							borderRadius: "999px",
							px: 2.2,
							fontWeight: 900,
							borderColor: alpha(colors.blueAccent[400], 0.45),
							color: showStandaloneForm
								? "#fff"
								: colors.blueAccent[300],
							backgroundColor: showStandaloneForm
								? colors.blueAccent[500]
								: "transparent",
							"&:hover": {
								backgroundColor: showStandaloneForm
									? colors.blueAccent[400]
									: alpha(colors.blueAccent[500], 0.1),
							},
						}}
					>
						Promissória avulsa
					</Button>

					<Button
						variant="contained"
						disabled={!selectedKit}
						onClick={() => onConfirm(selectedKit)}
						sx={{
							borderRadius: "999px",
							px: 2.5,
							fontWeight: 900,
							background:
								theme.palette.mode === "dark"
									? `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`
									: `linear-gradient(135deg, ${colors.greenAccent[300]} 0%, ${colors.blueAccent[400]} 100%)`,
							color: "#fff",
							boxShadow: `0 10px 24px ${alpha(colors.greenAccent[900], 0.24)}`,
							"&:hover": {
								background:
									theme.palette.mode === "dark"
										? `linear-gradient(135deg, ${colors.greenAccent[400]} 0%, ${colors.blueAccent[400]} 100%)`
										: `linear-gradient(135deg, ${colors.greenAccent[200]} 0%, ${colors.blueAccent[300]} 100%)`,
							},
							"&.Mui-disabled": {
								color: alpha("#fff", 0.55),
								background: alpha(colors.grey[500], 0.3),
							},
						}}
					>
						Gerar contrato + promissória
					</Button>
					{showStandaloneForm && (
						<Button
							variant="contained"
							onClick={handleConfirmStandalone}
							startIcon={<ReceiptLongRoundedIcon />}
							sx={{
								borderRadius: "999px",
								px: 2.5,
								fontWeight: 900,
								background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.pink[500]} 100%)`,
								color: "#fff",
								boxShadow: `0 10px 24px ${alpha(
									colors.blueAccent[900],
									0.24
								)}`,
								"&:hover": {
									background: `linear-gradient(135deg, ${colors.blueAccent[400]} 0%, ${colors.pink[400]} 100%)`,
								},
							}}
						>
							Gerar contrato + avulsa
						</Button>
					)}
				</Box>
			</DialogActions>
		</Dialog>
	);
};

export default SelectKitPromissoryModal;